import { memo, useCallback, useEffect, useRef } from "react";

import { noop } from "lodash-es";
import { twJoin } from "tailwind-merge";

import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { DormantCodeInput } from "src/matrixTableNode/DormantCodeInput";
import { useFloatingCodeInput } from "src/matrixTableNode/FloatingCodeInputProvider";
import {
  useMatrixTableCellEditing,
  useMatrixTableCellHighlighted,
  useMatrixTableCellSelected,
  useMatrixTableError,
  useMatrixTableNavActions,
} from "src/matrixTableNode/stores";

const EffectCellComponent = ({
  searchHighlightKey,
  value,
  rowIndex,
  colIndex,
  readOnly,
}: {
  searchHighlightKey: string;
  value: string;
  rowIndex: number;
  colIndex: number;
  readOnly: boolean;
}) => {
  const { attach } = useFloatingCodeInput();
  const isSearchHighlighted = useIsFieldHighlighted(searchHighlightKey);
  const ref = useRef<HTMLDivElement>(null);
  const isSelected = useMatrixTableCellSelected({
    row: rowIndex,
    col: colIndex,
  });
  const isHighlighted = useMatrixTableCellHighlighted({
    row: rowIndex,
    col: colIndex,
  });
  const error = useMatrixTableError({ row: rowIndex, col: colIndex });
  const { editCell, hoverCell } = useMatrixTableNavActions();
  const isEditing = useMatrixTableCellEditing({
    row: rowIndex,
    col: colIndex,
  });

  const handleClick = useCallback(() => {
    editCell({ row: rowIndex, col: colIndex });
  }, [rowIndex, colIndex, editCell]);

  useEffect(() => {
    if (isEditing) {
      attach({
        ref,
        mode: "effect",
        path: `grid_effects.${rowIndex}.${colIndex - 1}.value`, // -1 because first column is row predicate
      });
    }
  }, [isEditing, attach, rowIndex, colIndex, ref]);

  useEffect(() => {
    if (isSelected) {
      ref.current?.scrollIntoView({
        behavior: "instant",
        block: "nearest",
      });
    }
  }, [isSelected]);

  return (
    <div
      className={twJoin(
        "flex h-inherit items-center border-green-100 border-r border-b",
        readOnly && "cursor-not-allowed bg-gray-100",
      )}
      data-loc={`matrix-table-node-effect-cell-${rowIndex}-${colIndex}`}
      onMouseEnter={() =>
        !readOnly && hoverCell({ row: rowIndex, col: colIndex })
      }
    >
      <DormantCodeInput
        ref={ref}
        error={error}
        isHighlighted={isHighlighted}
        isSearchHighlighted={isSearchHighlighted}
        isSelected={isSelected && !isEditing}
        readOnly={readOnly}
        value={value}
        variant="effect"
        onClick={readOnly ? noop : handleClick}
      />
    </div>
  );
};

export const EffectCell = memo(EffectCellComponent);
